/** `/catalog`: entry point for products, prizes, brands and sponsors with a §6.2 stock reconciliation view. */
import { useQuery } from '@tanstack/react-query';
import { Button } from '@windrop/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { Can } from '../../auth/guards';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { lotteriesApi } from '../../lib/api/admin';
import { catalogApi } from './api';

export function CatalogHomePage() {
  const prizes = useQuery({ queryKey: ['prizes', {}], queryFn: () => catalogApi.prizes.list() });
  const products = useQuery({ queryKey: ['products'], queryFn: () => catalogApi.products.list() });
  const sponsors = useQuery({ queryKey: ['sponsors'], queryFn: () => catalogApi.sponsors.list() });
  const brands = useQuery({ queryKey: ['brands'], queryFn: () => catalogApi.brands.list() });
  const lotteries = useQuery({ queryKey: ['lotteries', {}], queryFn: () => lotteriesApi.list() });
  const [lotteryId, setLotteryId] = useState('');
  const recon = useQuery({
    queryKey: ['stock-reconciliation', lotteryId],
    queryFn: () => catalogApi.prizes.reconciliation(lotteryId),
    enabled: Boolean(lotteryId),
  });
  const count = (q: { data?: unknown[]; isPending: boolean }) => (q.isPending ? null : (q.data?.length ?? 0));
  return (
    <>
      <PageHeader
        title="Sponsors & prizes"
        description="Products with option axes and SKU stock, prizes attached to lotteries, Mode B brand approvals and sponsor brands (§6, §7)."
        breadcrumb={[{ label: 'Sponsors & prizes' }]}
        actions={
          <Can permission="prizes.manage">
            <Button href="/catalog/products/new">New product</Button>
          </Can>
        }
      />
      <div className="wdc-grid">
        <StatCard
          label="Products"
          value={count(products)}
          loading={products.isPending}
          action={<Link to="/catalog/products">Open</Link>}
        />
        <StatCard
          label="Prizes"
          value={count(prizes)}
          loading={prizes.isPending}
          action={<Link to="/catalog/prizes">Open</Link>}
        />
        <StatCard
          label="Approved brands"
          value={brands.isPending ? null : (brands.data?.filter((b) => b.approved).length ?? 0)}
          loading={brands.isPending}
          action={<Link to="/catalog/brands">Open</Link>}
        />
        <StatCard
          label="Sponsors"
          value={count(sponsors)}
          loading={sponsors.isPending}
          action={<Link to="/catalog/sponsors">Open</Link>}
        />
      </div>
      <h2>Stock reconciliation (§6.2)</h2>
      <label className="wd-field" style={{ maxWidth: 360 }}>
        <span className="wd-field__label">Lottery</span>
        <select
          className="wd-select"
          value={lotteryId}
          onChange={(e) => setLotteryId(e.target.value)}
          data-testid="recon-lottery"
        >
          <option value="">Choose a lottery</option>
          {(lotteries.data?.items ?? []).map((l) => (
            <option key={l.lotteryId} value={l.lotteryId}>
              {l.title}
            </option>
          ))}
        </select>
      </label>
      {recon.data ? (
        <table className="wdc-table" data-testid="recon-table">
          <caption>
            {recon.data.balanced
              ? 'Every prize reconciles.'
              : 'A prize does not reconcile — investigate before publishing.'}
          </caption>
          <thead>
            <tr>
              <th>Prize</th>
              <th>Catalogue</th>
              <th>Main reserved</th>
              <th>Sampling reserved</th>
              <th>Held</th>
              <th>Won</th>
              <th>Paid / delivered</th>
              <th>Available</th>
              <th>Balanced</th>
            </tr>
          </thead>
          <tbody>
            {recon.data.prizes.map((p) => (
              <tr key={p.prizeId}>
                <td>
                  <Link to={`/catalog/prizes/${p.prizeId}`}>{p.name}</Link>
                </td>
                <td>{p.stock.catalogStock}</td>
                <td>{p.stock.mainReserved}</td>
                <td>{p.stock.samplingReserved}</td>
                <td>{p.stock.held}</td>
                <td>{p.stock.won}</td>
                <td>{p.stock.fulfilled}</td>
                <td>{p.stock.available}</td>
                <td>{p.balanced ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </>
  );
}
